import { useAnimatedDomain, type ChartAxisDomain } from './useAnimatedDomain';

interface TimeXAxisProps {
  dataKey: 'x';
  type: 'number';
  scale: 'time';
  /** The animated domain — falls back to the data extent when no bounded window is supplied. */
  domain: ChartAxisDomain | undefined;
  /** Clip to the supplied window rather than widening back to the data extent — only when bounded. */
  allowDataOverflow: boolean;
}

/**
 * Ready-to-spread recharts `XAxis` props for a chart's shared time axis. Owns the whole convention in
 * one place — the epoch-ms time scale, the {@link useAnimatedDomain} zoom tween, and clipping to a
 * bounded window — so every time chart spreads `{...useTimeXAxisProps(domain)}` and gets animation +
 * overflow handling for free rather than re-deriving them (and risking forgetting one). Spread onto a
 * real `<XAxis>` element, not a wrapper component — recharts detects axes by child element type.
 *
 * `domain` is the bounded epoch-ms window (e.g. the timeline's zoom range), or `undefined` to auto-fit
 * the data. Callers still supply presentational props (`hide`, `tickFormatter`, …) on the element.
 */
export function useTimeXAxisProps(domain: [number, number] | undefined): TimeXAxisProps {
  return {
    dataKey: 'x',
    type: 'number',
    scale: 'time',
    domain: useAnimatedDomain(domain ?? ['dataMin', 'dataMax']),
    allowDataOverflow: domain !== undefined,
  };
}
